import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { SalesOrderLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { Link, useNavigate } from 'react-router-dom';
import { useSearchCondition } from '@/providers/SearchConditionProvider';
import { SalesOrder, Team, TeamSalesOrderPhase } from '@/hooks/api/models';
import { useTeamSalesOrderPhases } from '@/hooks/api/0250_team_sales_order_phases';
import { useLoading } from '@/providers/LoadingProvider';
import { useSalesOrders } from '@/hooks/api/1210_sales_orders';
import { useUpdateSalesOrderPhase } from '@/hooks/api/1270_update_sales_order_phase';
import { toastError, toastSuccess } from '@/lib/toast';
import {
  Active,
  closestCenter,
  closestCorners,
  CollisionDetection,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  getFirstCollision,
  MeasuringStrategy,
  MouseSensor,
  pointerWithin,
  rectIntersection,
  UniqueIdentifier,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { dateFormat } from '@/utils';
import { SalesOrderSearchConditionTags } from '@/features/w/[identifier]/teams/[team_id]/sales_orders/{components}/SalesOrderSearchConditionTags';
import { SalesOrderFilterModal } from '@/features/w/[identifier]/teams/[team_id]/sales_orders/{components}/SalesOrderFilterModal';
import { SalesOrdersListElement } from '@/features/w/[identifier]/teams/[team_id]/sales_orders/{components}/SalesOrdersListElement';
import { useWindowSize } from '@/hooks/useWindowSize';
import { salesOrderInvalidate, useSalesOrderInvalidate } from '@/hooks/api/1260_sales_order';
import { SimpleTextFormat } from '@/components/element/SimpleTextFormat';
import { ModalMessage } from '@/components/element/modal/ModalMessage';
import { useIntercomGuide } from '@/hooks/useIntercomGuide';

export const TeamSalesOrders = () => {
  const { setTitle, setBreadcrumbs } = useNavState();
  const { _w_, _t_, meTeamMember } = useCurrentTeamMember();

  const {
    salesOrderDisplayType: displayType,
    setSalesOrderDisplayType: setDisplayType,
    setSalesOrderSearchCondition: setSearchCondition,
  } = useSearchCondition();
  const [curTeam, setCurTeam] = useState<Team>();

  useEffect(() => setTitle(`プロジェクト（売上） - ${curTeam?.name}`), [curTeam]);
  useEffect(() => setBreadcrumbs([]), []);

  useIntercomGuide();

  useEffect(() => {
    if (_t_! != curTeam) {
      // 1ページ目に戻す
      setSearchCondition((state) => ({ ...state, page: 1 }));
      setCurTeam(_t_!);
    }
  }, [_t_, displayType]);

  // チームを切り替えた際にSearchConditionの切り替え前にレンダリングされるのを防ぐため
  if (_t_! != curTeam) return <></>;

  return (
    <WorkspaceLayout>
      <div className="container-fluid py-4">
        <div className="d-flex mb-4 align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="btn-group">
              <button
                className={`btn btn-icon ${displayType == 'list' ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => setDisplayType('list')}
              >
                <i className="fas fa-th-list"></i>リスト
              </button>
              <button
                className={`btn btn-icon ${displayType == 'kanban' ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => setDisplayType('kanban')}
              >
                <i className="fas fa-th-large"></i>ボード
              </button>
            </div>
            <button className="btn btn-icon-only btn-dark mx-2" data-bs-toggle="modal" data-bs-target="#modal-filter">
              <i className="fas fa-filter"></i>
            </button>
          </div>
          <div className="ms-auto d-flex justify-content-end align-items-center">
            <div className="pe-4 position-relative">
              <p className="text-secondary text-xs font-weight-bold mb-2">チームメンバー</p>
              <div className="d-flex align-items-center justify-content-center">
                <div className="avatar-group text-nowrap overflow-hidden flex-1">
                  {_t_!.team_members.map((tm) => (
                    <img
                      key={`tm-icon-${tm.id}`}
                      src={tm.user.user_account.icon_file_url}
                      className="avatar avatar-xs rounded-circle"
                    />
                  ))}
                </div>
              </div>
              <hr className="vertical dark mt-0" />
            </div>
            <div className="ps-4">
              {TeamMemberLogic.canCreateSalesOrder(_t_!, meTeamMember!) && (
                <Link
                  to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/new`}
                  className="btn bg-gradient-primary btn-icon mb-0"
                  data-toggle="modal"
                  data-target="#new-board-modal"
                >
                  <i className="fa fa-plus"></i> プロジェクト（売上）新規作成
                </Link>
              )}
            </div>
          </div>
        </div>

        <SalesOrderSearchConditionTags />

        <SalesOrderFilterModal id="modal-filter" />

        {displayType == 'list' && <SalesOrdersListElement />}
        {displayType == 'kanban' && <SalesOrdersKanbanElement />}
      </div>
    </WorkspaceLayout>
  );
};

type KanbanBoardType = {
  id: UniqueIdentifier;
  data: TeamSalesOrderPhase;
  title: string;
  items: KanbanItemType[];
  activeId?: UniqueIdentifier | null;
};

type KanbanItemType = {
  id: UniqueIdentifier;
  data: SalesOrder;
  title: string;
  activeId?: UniqueIdentifier | null;
};

const SalesOrdersKanbanElement = () => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { data: phases } = useTeamSalesOrderPhases({ identifier: _w_!.identifier, team_id: _t_!.id });
  if (phases) return <_SalesOrdersKanbanElement phases={phases} />;
  else return <></>;
};

const _SalesOrdersKanbanElement = ({ phases }: { phases: TeamSalesOrderPhase[] }) => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const { setShowLoading } = useLoading();
  const { salesOrderSearchCondition: searchCondition } = useSearchCondition();
  const [allLoaded, setAllLoaded] = useState(false);

  const baseSearchCondition = {
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    per: 1000,
    ...searchCondition,
    page: 1,
  };
  const ordersPhase1SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[0].id] : [],
  };
  const ordersPhase2SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[1].id] : [],
  };
  const ordersPhase3SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[2].id] : [],
  };
  const ordersPhase4SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[3].id] : [],
  };
  const ordersPhase5SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[4].id] : [],
  };
  const ordersPhase6SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[5].id] : [],
  };
  const ordersPhase7SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[6].id] : [],
  };
  const ordersPhase8SearchCondition = {
    ...baseSearchCondition,
    enabled: false,
    team_sales_order_phase_ids: phases ? [phases[7].id] : [],
  };

  const { data: ordersPhase1Resp, refetch: ordersPhase1Refetch } = useSalesOrders(ordersPhase1SearchCondition);
  const { data: ordersPhase2Resp, refetch: ordersPhase2Refetch } = useSalesOrders(ordersPhase2SearchCondition);
  const { data: ordersPhase3Resp, refetch: ordersPhase3Refetch } = useSalesOrders(ordersPhase3SearchCondition);
  const { data: ordersPhase4Resp, refetch: ordersPhase4Refetch } = useSalesOrders(ordersPhase4SearchCondition);
  const { data: ordersPhase5Resp, refetch: ordersPhase5Refetch } = useSalesOrders(ordersPhase5SearchCondition);
  const { data: ordersPhase6Resp, refetch: ordersPhase6Refetch } = useSalesOrders(ordersPhase6SearchCondition);
  const { data: ordersPhase7Resp, refetch: ordersPhase7Refetch } = useSalesOrders(ordersPhase7SearchCondition);
  const { data: ordersPhase8Resp, refetch: ordersPhase8Refetch } = useSalesOrders(ordersPhase8SearchCondition);

  const [boards, setBoards] = useState<KanbanBoardType[]>(
    phases.map((phase) => ({
      id: `b-${phase.id}`,
      data: phase,
      title: phase.label,
      items: [] as KanbanItemType[],
    }))
  );

  useEffect(() => setShowLoading(!allLoaded), [allLoaded]);

  const fetchAll = () => {
    setAllLoaded(false);

    ordersPhase1Refetch();
    ordersPhase2Refetch();
    ordersPhase3Refetch();
    ordersPhase4Refetch();
    ordersPhase5Refetch();
    ordersPhase6Refetch();
    ordersPhase7Refetch();
    ordersPhase8Refetch();
  };

  useEffect(() => {
    fetchAll();
  }, [searchCondition]);

  useEffect(() => {
    if (
      ordersPhase1Resp &&
      ordersPhase2Resp &&
      ordersPhase3Resp &&
      ordersPhase4Resp &&
      ordersPhase5Resp &&
      ordersPhase6Resp &&
      ordersPhase7Resp &&
      ordersPhase8Resp
    ) {
      const responses = [
        ordersPhase1Resp,
        ordersPhase2Resp,
        ordersPhase3Resp,
        ordersPhase4Resp,
        ordersPhase5Resp,
        ordersPhase6Resp,
        ordersPhase7Resp,
        ordersPhase8Resp,
      ];
      setBoards((prevState) =>
        prevState.map((b, idx) => {
          b.items = responses[idx].sales_orders.map((so) => ({
            id: `i-${so.id}`,
            data: so,
            title: so.name,
          }));
          return b;
        })
      );

      setAllLoaded(true);
    }
  }, [
    ordersPhase1Resp,
    ordersPhase2Resp,
    ordersPhase3Resp,
    ordersPhase4Resp,
    ordersPhase5Resp,
    ordersPhase6Resp,
    ordersPhase7Resp,
    ordersPhase8Resp,
  ]);

  if (!allLoaded) return <></>;

  return <KanbanContainer boards={boards} setBoards={setBoards} />;
};

const KanbanContainer = ({
  boards,
  setBoards,
}: {
  boards: KanbanBoardType[];
  setBoards: Dispatch<SetStateAction<KanbanBoardType[]>>;
}) => {
  const { _w_, _t_ } = useCurrentTeamMember();
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const { mutate, data, error, isSuccess } = useUpdateSalesOrderPhase();

  const lastOverId = useRef<UniqueIdentifier | null>(null);
  const recentlyMovedToNewContainer = useRef(false);
  const [width, height] = useWindowSize();
  const kanbanContainerRef = useRef<HTMLDivElement>(null);
  const showMessageButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (kanbanContainerRef.current) {
      const cTop = kanbanContainerRef.current.offsetTop;
      kanbanContainerRef.current.style.height = `${height - cTop - 80}px`;
    }
  }, [kanbanContainerRef, width, height]);

  useEffect(() => {
    if (data) {
      salesOrderInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: data.id });

      if ('契約未承認' == data.alert) {
        // フェーズ移動でアラートが設定された場合にモーダルでメッセージ表示
        showMessageButtonRef.current?.click();
      }

      // 更新されたプロジェクトの差し替え
      setBoards((prevState) =>
        prevState.map((b) => {
          b.items.map((item) => {
            if (item.data.id == data.id) {
              item.data = data;
            }
            return item;
          });
          return b;
        })
      );
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      const msgs: string[] = (error as any).response.data.errors || [];
      if (msgs.length > 0) toastError(msgs[0]);
    }
  }, [error]);

  const sensors = useSensors(useSensor(MouseSensor, { activationConstraint: { distance: 5 } }));

  const findBoard = useCallback(
    (unique: string | null) => {
      if (!unique) {
        return null;
      }
      // overの対象がcolumnの場合があるためそのままidを返す
      if (boards.some((b) => b.id === unique)) {
        return boards.find((b) => b.id === unique) ?? null;
      }
      const id = String(unique);
      const itemWithColumnId = boards.flatMap((b) => {
        const boardId = b.id;
        return b.items.map((i) => ({ itemId: i.id, boardId: boardId }));
      });
      const columnId = itemWithColumnId.find((i) => i.itemId === id)?.boardId;
      return boards.find((b) => b.id === columnId) ?? null;
    },
    [boards]
  );

  const findItem = useCallback(
    (id: UniqueIdentifier): KanbanItemType | undefined => {
      let res: KanbanItemType | undefined;
      boards.forEach((b) => {
        if (res != null) return;

        const _i = b.items.find((i) => i.id == id);
        if (_i) res = _i;
      });
      return res;
    },
    [boards]
  );

  // ドラッグ開始時に発火する関数
  const handleDragStart = useCallback(
    ({ active }: { active: Active }) => {
      setActiveId(active.id);
    },
    [boards]
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over, delta } = event;
      const activeId = String(active.id);
      const overId = over ? String(over.id) : null;
      const activeColumn = findBoard(activeId);
      const overColumn = findBoard(overId);
      if (!activeColumn || !overColumn || activeColumn === overColumn) {
        return null;
      }
      setBoards((prevState) => {
        const activeItems = activeColumn.items;
        const overItems = overColumn.items;
        const activeIndex = activeItems.findIndex((i) => i.id === activeId);
        const overIndex = overItems.findIndex((i) => i.id === overId);

        recentlyMovedToNewContainer.current = true;

        const newIndex = () => {
          const putOnBelowLastItem = overIndex === overItems.length - 1 && delta.y > 0;
          const modifier = putOnBelowLastItem ? 1 : 0;
          return overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        };
        return prevState.map((c) => {
          if (c.id === activeColumn.id) {
            c.items = activeItems.filter((i) => i.id !== activeId);
            return c;
          } else if (c.id === overColumn.id) {
            c.items = [
              ...overItems.slice(0, newIndex()),
              activeItems[activeIndex],
              ...overItems.slice(newIndex(), overItems.length),
            ];
            return c;
          } else {
            return c;
          }
        });
      });
    },
    [boards]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      const activeId = String(active.id);
      const overId = over ? String(over.id) : null;

      setActiveId(null);

      const activeColumn = findBoard(activeId);
      const overColumn = findBoard(overId);
      if (!activeColumn || !overColumn || activeColumn !== overColumn) {
        return null;
      }
      const activeIndex = activeColumn.items.findIndex((i) => i.id === activeId);
      const overIndex = overColumn.items.findIndex((i) => i.id === overId);
      if (activeIndex !== overIndex) {
        setBoards((prevState) => {
          return prevState.map((column) => {
            if (column.id === activeColumn.id) {
              column.items = arrayMove(overColumn.items, activeIndex, overIndex);
              return column;
            } else {
              return column;
            }
          });
        });
      }

      const movedItem = findItem(activeId);
      if (movedItem) {
        if (movedItem.data.team_sales_order_phase.id != overColumn.data.id) {
          mutate({
            identifier: _w_!.identifier,
            team_id: _t_!.id,
            sales_order_id: movedItem.data.id,
            team_sales_order_phase_id: overColumn.data.id,
          });
        }
      }
    },
    [boards]
  );

  const handleDragCancel = () => {
    setActiveId(null);
    useEffect(() => {
      requestAnimationFrame(() => (recentlyMovedToNewContainer.current = false));
    }, [boards]);
  };

  const collisionDetectionStrategy: CollisionDetection = useCallback(
    (args) => {
      if (activeId && activeId in boards) {
        return closestCenter({
          ...args,
          droppableContainers: args.droppableContainers.filter((container) => container.id in boards),
        });
      }

      // Start by finding any intersecting droppable
      const pointerIntersections = pointerWithin(args);
      const intersections =
        pointerIntersections.length > 0
          ? // If there are droppables intersecting with the pointer, return those
            pointerIntersections
          : rectIntersection(args);
      let overId = getFirstCollision(intersections, 'id');

      if (overId != null) {
        if (boards.some((b) => b.id == overId)) {
          const containerItems = boards.find((b) => b.id == overId)?.items;

          if (containerItems && containerItems.length > 0) {
            overId = closestCenter({
              ...args,
              droppableContainers: args.droppableContainers.filter(
                (container) => container.id !== overId && containerItems.some((i) => i.id == container.id)
              ),
            })[0]?.id;
          }
        }

        return [{ id: overId }];
      }

      // When a draggable item moves to a new container, the layout may shift
      // and the `overId` may become `null`. We manually set the cached `lastOverId`
      // to the id of the draggable item that was moved to the new container, otherwise
      // the previous `overId` will be returned which can cause items to incorrectly shift positions
      if (recentlyMovedToNewContainer.current) {
        lastOverId.current = activeId;
      }

      // If no droppable is matched, return the last match
      return lastOverId.current ? [{ id: lastOverId.current }] : [];
    },
    [activeId, boards]
  );

  return (
    <>
      <ModalMessage
        modalId="messageModal"
        message="承認された契約申請（売上）がありません"
        showModalButtonRef={showMessageButtonRef}
      />

      <DndContext
        sensors={sensors}
        // collisionDetection={closestCorners}
        collisionDetection={collisionDetectionStrategy}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.Always,
          },
        }}
      >
        <div id="myKanban" className="mb-4" style={{ overflowX: 'auto' }}>
          <div className="kanban-container" style={{ width: 2480 }} ref={kanbanContainerRef}>
            {boards.map((b) => {
              return <KanbanBoard key={`board-${b.id}`} {...b} activeId={activeId} />;
            })}
          </div>
          <DragOverlay>{activeId ? <OverlayKanbanItem {...findItem(activeId)!} /> : null}</DragOverlay>
        </div>
      </DndContext>
    </>
  );
};

const DraggableKanbanItem = ({ id, activeId, ...props }: KanbanItemType) => {
  const navigate = useNavigate();
  const { _w_, _t_ } = useCurrentTeamMember();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      className="kanban-item border-radius-md"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={id == activeId ? { ...style, opacity: 0.4 } : style}
      onClick={() => navigate(`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${props.data.id}`)}
    >
      <KanbanItemElement id={id} title={props.title} data={props.data} />
    </div>
  );
};

const OverlayKanbanItem = (props: KanbanItemType) => {
  return (
    <div className="kanban-item border-radius-md" style={{ transform: 'rotate(2deg)', opacity: 0.8 }}>
      <KanbanItemElement {...props} />
    </div>
  );
};

const KanbanItemElement = ({ title, data }: KanbanItemType) => {
  const { _w_, _t_ } = useCurrentTeamMember();

  return (
    <>
      {data.alert && (
        <div className="mb-2">
          <span className="badge badge-xs badge-danger">{data.alert}</span>
        </div>
      )}
      <div className="mb-1">
        <span className="badge badge-xs bg-gradient-primary me-1 text-truncate">
          {data.business_partner.display_name}
        </span>
        {data.agency && (
          <span className="badge badge-xs bg-gradient-info me-1 text-truncate">{data.agency.display_name}</span>
        )}
      </div>
      <h6 className="text-truncate text-sm mb-0">
        <Link to={`/w/${_w_!.identifier}/teams/${_t_!.id}/sales_orders/${data.id}`}>{title}</Link>
      </h6>
      <div className="avatar-group mb-2">
        <img src={data.user.user_account.icon_file_url} className="avatar avatar-xs rounded-circle me-1" />
        <span className="text-xs text-dark text-truncate">{data.user.user_account.name}</span>
        {/*{data.sales_order_members.map((m) => (*/}
        {/*  <img key={`kanban-item-member-img-${m.id}`} src={m.user.user_account.icon_file_url}*/}
        {/*       className='avatar avatar-xs rounded-circle me-1' />*/}
        {/*))}*/}
      </div>
      <div className="d-flex flex-wrap">
        <span className="badge badge-xs bg-gradient-success me-1 mb-1 text-truncate">
          ¥{data.accounting_total_amount.toLocaleString()}
        </span>
        <span className="badge badge-xs bg-gradient-secondary me-1 mb-1 text-truncate">{data.product.name}</span>
      </div>
      <hr className="horizontal dark my-2" />
      <div className="d-flex justify-content-between">
        <div>
          <div className="font-weight-bold text-dark text-xs">{data.sales_order_comments_count}</div>
          <div className="text-secondary text-xxs">コメント</div>
        </div>
        <div className="text-end">
          <div className="font-weight-bold text-dark text-xs">{dateFormat({ date: data.updated_at })}</div>
          <div className="text-secondary text-xxs">最終更新日</div>
        </div>
      </div>
    </>
  );
};

const KanbanBoard = ({ id, title, items, activeId }: KanbanBoardType) => {
  const { setNodeRef } = useDroppable({ id });

  return (
    <SortableContext items={items} strategy={rectSortingStrategy}>
      <div className="kanban-board" style={{ width: 300, marginLeft: 10, marginTop: 10 }}>
        <header className="kanban-board-header">
          <div className="kanban-title-board">{title}</div>
        </header>
        <div ref={setNodeRef} className="kanban-drag" style={{ height: '100%' }}>
          {items.map((i) => {
            return <DraggableKanbanItem key={`kanban-${i.data.id}`} {...i} activeId={activeId} />;
          })}
        </div>
      </div>
    </SortableContext>
  );
};
